import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Grid, Paper, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';
import Swal from 'sweetalert2';
import { api } from '../../../api';
import RichTextEditor from 'components/RichTextEditor';

function PlanAndCauseSection({ procedureId, existingDetail, canSave, onDetailSaved }) {
  const theme = useTheme();
  const primaryColor = theme.palette.logo?.main || '#00798E';

  const [causeOfInfertility, setCauseOfInfertility] = useState('');
  const [plan, setPlan]                             = useState('');
  const [saving, setSaving]                         = useState(false);
  const [errors, setErrors]                         = useState({ causeOfInfertility: false, plan: false });
  // Tracks the latest saved snapshot for the read-only display
  const [currentDetail, setCurrentDetail]           = useState(null);

  useEffect(() => {
    if (existingDetail) {
      setCauseOfInfertility(existingDetail.cause_of_infertility || '');
      setPlan(existingDetail.plan || '');
      setCurrentDetail(existingDetail);
    }
  }, [existingDetail]);

  const handleSave = async () => {
    const newErrors = {
      causeOfInfertility: !causeOfInfertility || causeOfInfertility.replace(/<[^>]*>/g, '').trim() === '',
      plan: !plan || plan.replace(/<[^>]*>/g, '').trim() === '',
    };
    setErrors(newErrors);

    if (newErrors.causeOfInfertility || newErrors.plan) {
      Swal.fire({
        icon: 'warning',
        title: 'Required Fields',
        text: 'Both Cause of Infertility and Plan are required.',
        timer: 3000,
        showConfirmButton: false,
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        cause_of_infertility: causeOfInfertility,
        plan,
      };

      // Always POST — backend inactivates the old record and creates a fresh one
      const response = await api.ivf.saveProcedureDetail(procedureId, payload);

      const saved = {
        id:                   response?.data?.id || null,
        cause_of_infertility: causeOfInfertility,
        plan,
      };
      setCurrentDetail(saved);
      if (onDetailSaved) onDetailSaved(saved);

      Swal.fire({
        icon: 'success',
        title: 'Saved',
        text: currentDetail
          ? 'Record updated. Previous entry has been archived.'
          : 'Plan and Cause of Infertility saved successfully.',
        timer: 2500,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('Error saving plan/cause:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'Failed to save. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      sx={{
        mb: 0,
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${primaryColor}30`,
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          px: 2, py: 1.5,
          borderBottom: `2px solid ${primaryColor}`,
          backgroundColor: `${primaryColor}0d`,
        }}
      >
        <Typography sx={{ color: primaryColor, fontWeight: 'bold', fontSize: '1rem' }}>
          Plan &amp; Cause of Infertility
        </Typography>
      </Box>

      {/* Read-only display of currently saved record */}
      {currentDetail && (
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: primaryColor, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Current Saved Record
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
                Cause of Infertility
              </Typography>
              <Box
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  minHeight: 56,
                  backgroundColor: '#fafafa',
                  '& p': { margin: '2px 0' },
                  '& ul, & ol': { paddingLeft: 20, margin: '4px 0' },
                }}
                dangerouslySetInnerHTML={{ __html: currentDetail.cause_of_infertility || '<em style="color:#aaa">—</em>' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
                Plan
              </Typography>
              <Box
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  minHeight: 56,
                  backgroundColor: '#fafafa',
                  '& p': { margin: '2px 0' },
                  '& ul, & ol': { paddingLeft: 20, margin: '4px 0' },
                }}
                dangerouslySetInnerHTML={{ __html: currentDetail.plan || '<em style="color:#aaa">—</em>' }}
              />
            </Grid>
          </Grid>

          {canSave !== false && (
            <Divider sx={{ mt: 2, mb: 0 }}>
              <Typography sx={{ fontSize: '0.72rem', color: '#999', px: 1 }}>Update Record</Typography>
            </Divider>
          )}
        </Box>
      )}

      {/* Editable form */}
      {canSave !== false && (
        <Box sx={{ p: 2, pt: currentDetail ? 1.5 : 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <RichTextEditor
                label="Cause of Infertility *"
                value={causeOfInfertility}
                onChange={(val) => { setCauseOfInfertility(val); if (errors.causeOfInfertility) setErrors(prev => ({ ...prev, causeOfInfertility: false })); }}
                height="180px"
              />
              {errors.causeOfInfertility && (
                <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 0.5 }}>
                  Cause of Infertility is required
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <RichTextEditor
                label="Plan *"
                value={plan}
                onChange={(val) => { setPlan(val); if (errors.plan) setErrors(prev => ({ ...prev, plan: false })); }}
                height="180px"
              />
              {errors.plan && (
                <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 0.5 }}>
                  Plan is required
                </Typography>
              )}
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            {currentDetail ? (
              <Typography sx={{ fontSize: '0.72rem', color: '#999', fontStyle: 'italic' }}>
                Saving will archive the current record and create a new entry.
              </Typography>
            ) : <span />}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '10px 28px',
                background: saving ? '#ccc' : primaryColor,
                color: saving ? '#999' : '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'background 0.3s ease',
              }}
              onMouseOver={(e) => { if (!saving) e.currentTarget.style.background = '#006379'; }}
              onMouseOut={(e)  => { if (!saving) e.currentTarget.style.background = primaryColor; }}
            >
              <SaveIcon fontSize="small" />
              {saving ? 'Saving...' : currentDetail ? 'Update' : 'Save'}
            </button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

PlanAndCauseSection.propTypes = {
  procedureId:    PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  existingDetail: PropTypes.shape({
    id:                   PropTypes.number,
    cause_of_infertility: PropTypes.string,
    plan:                 PropTypes.string,
  }),
  canSave:        PropTypes.bool,
  onDetailSaved:  PropTypes.func,
};

PlanAndCauseSection.defaultProps = {
  existingDetail: null,
  canSave:        true,
  onDetailSaved:  null,
};

export default PlanAndCauseSection;
